import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Container,
  Divider,
  Group,
  Loader,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconCheck,
  IconCopy,
  IconGiftCard,
  IconX,
} from '@tabler/icons-react';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { obtenerCamposBusquedaGiftcard } from '../utils/giftcardSearch';
import { useAuth } from '../contexts/AuthContext';

const formatearMonto = (monto = 0) => Number(monto || 0).toLocaleString('es-CL');

const normalizarTexto = (value = '') => String(value ?? '').trim().replace(/\s+/g, ' ');

const normalizarEmail = (value = '') => normalizarTexto(value).toLowerCase();

const getFechaCompra = () => new Date().toISOString().split('T')[0];

const getFechaLegible = (value) => {
  if (!value) return 'Sin fecha';
  const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sin fecha';
  return date.toLocaleDateString('es-CL', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusMeta = (status) => {
  const map = {
    pending_payment: { label: 'Pendiente de pago', color: 'yellow' },
    paid: { label: 'Pagada', color: 'green' },
    converted: { label: 'Convertida', color: 'blue' },
    cancelled: { label: 'Cancelada', color: 'red' },
    payment_error: { label: 'Error al iniciar pago', color: 'red' },
    payment_rejected: { label: 'Pago rechazado', color: 'red' },
    payment_cancelled: { label: 'Pago cancelado', color: 'gray' },
    refunded: { label: 'Reembolsada', color: 'orange' },
    charged_back: { label: 'Contracargo', color: 'red' },
  };

  return map[status] || { label: 'Sin estado', color: 'gray' };
};

const getRequestSummary = (request) => {
  const customer = request.customer || {};
  const recipient = request.recipient || {};
  const items = request.items || [];
  const message = normalizarTexto(recipient.message);

  return [
    'Solicitud de giftcard Club de la Barba',
    `ID: ${request.id}`,
    `Cliente: ${customer.name || 'Sin nombre'}`,
    `Telefono: ${customer.phone || 'Sin telefono'}`,
    `Email: ${customer.email || 'Sin email'}`,
    `Regalo de: ${recipient.from || customer.name || 'Sin dato'}`,
    `Para: ${recipient.name || 'Sin dato'}`,
    `Email destinatario: ${recipient.email || 'Sin email'}`,
    ...(message ? [`Mensaje: ${message}`] : []),
    '',
    'Servicios:',
    ...items.map((item) => `- ${item.name} x${item.quantity} - $${formatearMonto(item.lineTotal)}`),
    '',
    `Subtotal: $${formatearMonto(request.subtotal)}`,
    `Descuento: $${formatearMonto(request.discountAmount)}`,
    `Total: $${formatearMonto(request.total)}`,
    request.coupon?.code ? `Cupon: ${request.coupon.code} (${request.coupon.percentOff}%)` : 'Cupon: no aplicado',
  ].join('\n');
};

const createNumeroId = (requestId) => {
  const year = new Date().getFullYear();
  const suffix = String(requestId || Math.random().toString(36).slice(2))
    .replace(/[^a-z0-9]/gi, '')
    .slice(0, 6)
    .toUpperCase();
  const random = Math.floor(1000 + Math.random() * 9000);
  return `GC-${year}-${suffix}${random}`;
};

const GiftcardRequests = () => {
  const { currentUser } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [convertingId, setConvertingId] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);

  useEffect(() => {
    const requestsQuery = query(
      collection(db, 'giftcardRequests'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      requestsQuery,
      (snapshot) => {
        setRequests(snapshot.docs.map((requestDoc) => ({
          id: requestDoc.id,
          ...requestDoc.data(),
        })));
        setLoading(false);
        setError('');
      },
      (snapshotError) => {
        console.error('Error al cargar solicitudes de giftcards:', snapshotError);
        setError('No se pudieron cargar las solicitudes web de giftcards.');
        setLoading(false);
      }
    );

    return unsubscribe;
  }, []);

  const stats = useMemo(() => {
    const pending = requests.filter((request) => request.status === 'pending_payment').length;
    const converted = requests.filter((request) => request.status === 'converted').length;
    const totalPending = requests
      .filter((request) => request.status === 'pending_payment')
      .reduce((total, request) => total + Number(request.total || 0), 0);

    return { pending, converted, totalPending };
  }, [requests]);

  const handleCopySummary = async (request) => {
    try {
      await navigator.clipboard.writeText(getRequestSummary(request));
      notifications.show({
        title: 'Resumen copiado',
        message: 'El resumen está listo para pegarlo en WhatsApp.',
        color: 'green',
      });
    } catch (copyError) {
      console.error('Error al copiar resumen:', copyError);
      notifications.show({
        title: 'No se pudo copiar',
        message: 'Copia el resumen manualmente desde el detalle.',
        color: 'red',
      });
    }
  };

  const handleCancel = async (request) => {
    if (!window.confirm('¿Seguro que quieres cancelar esta solicitud?')) return;

    try {
      await updateDoc(doc(db, 'giftcardRequests', request.id), {
        status: 'cancelled',
        cancelledAt: serverTimestamp(),
        cancelledBy: currentUser?.email || '',
        updatedAt: serverTimestamp(),
      });

      notifications.show({
        title: 'Solicitud cancelada',
        message: 'La solicitud quedó marcada como cancelada.',
        color: 'yellow',
      });
    } catch (cancelError) {
      console.error('Error al cancelar solicitud:', cancelError);
      notifications.show({
        title: 'Error',
        message: 'No se pudo cancelar la solicitud.',
        color: 'red',
      });
    }
  };

  const handleConvert = async (request) => {
    if (!window.confirm('¿Confirmas que esta solicitud está pagada y quieres crear la giftcard?')) return;

    try {
      setConvertingId(request.id);
      const batch = writeBatch(db);
      const numeroId = createNumeroId(request.id);
      const giftcardIds = [];
      const customer = request.customer || {};
      const recipient = request.recipient || {};
      const items = request.items || [];
      const mensajeGiftcard = normalizarTexto(recipient.message);

      if (!items.length) {
        throw new Error('La solicitud no tiene servicios para convertir.');
      }

      items.forEach((item) => {
        const giftcardRef = doc(collection(db, 'giftcards'));
        giftcardIds.push(giftcardRef.id);
        const nombrePersona = normalizarTexto(recipient.name || customer.name);

        batch.set(giftcardRef, {
          tipo: 'giftcard',
          modoUso: 'servicio',
          servicio: normalizarTexto(item.name),
          precioUnidad: Number(item.unitPrice || 0),
          cantidad: Number(item.quantity || 1),
          nombrePersona,
          ...obtenerCamposBusquedaGiftcard({ numeroId, nombrePersona }),
          clienteEmail: normalizarEmail(customer.email),
          destinatarioEmail: normalizarEmail(recipient.email),
          regaloDe: normalizarTexto(recipient.from || customer.name || 'Club de la Barba'),
          regaloPara: normalizarTexto(recipient.name || nombrePersona),
          mensajeGiftcard,
          numeroId,
          fechaCompra: getFechaCompra(),
          fechaCreacion: serverTimestamp(),
          estado: 'activo',
          serviciosUsados: 0,
          creadoPor: currentUser?.email || '',
          historialUsos: [],
          origen: 'web',
          clasificacionComercial: 'pagada',
          campaignId: normalizarTexto(request.campaignId) || null,
          offerId: normalizarTexto(request.offerId) || null,
          requestId: request.id,
          totalGiftcard: Number(request.total || 0),
          descuentoGiftcard: Number(request.discountAmount || 0),
          cuponGiftcard: request.coupon || null,
        });
      });

      batch.update(doc(db, 'giftcardRequests', request.id), {
        status: 'converted',
        paidAt: serverTimestamp(),
        convertedAt: serverTimestamp(),
        convertedBy: currentUser?.email || '',
        numeroId,
        convertedGiftcardIds: giftcardIds,
        updatedAt: serverTimestamp(),
      });

      await batch.commit();

      notifications.show({
        title: 'Giftcard creada',
        message: `Se creó la giftcard ${numeroId} con ${giftcardIds.length} servicio(s).`,
        color: 'green',
      });
    } catch (convertError) {
      console.error('Error al convertir solicitud:', convertError);
      notifications.show({
        title: 'Error al convertir',
        message: convertError.message || 'No se pudo crear la giftcard.',
        color: 'red',
      });
    } finally {
      setConvertingId('');
    }
  };

  return (
    <Container size="xl" py="md">
      <Paper shadow="sm" p={{ base: 'md', md: 'xl' }} radius="xl" withBorder className="surface-panel">
        <Group justify="space-between" align="flex-start" mb="xl" gap="md">
          <Stack gap={6} style={{ flex: 1, minWidth: 260 }}>
            <Badge color="yellow" variant="light" w="fit-content">
              Giftcards web
            </Badge>
            <Title order={2} style={{ letterSpacing: '-0.03em' }}>
              Solicitudes web de giftcards
            </Title>
            <Text c="dimmed" maw={760}>
              Revisa solicitudes creadas desde la página, copia el resumen para cobrar y convierte las pagadas en giftcards reales.
            </Text>
          </Stack>
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md" mb="xl">
          <Card withBorder>
            <Text size="sm" c="dimmed" fw={700}>Pendientes</Text>
            <Title order={3}>{stats.pending}</Title>
          </Card>
          <Card withBorder>
            <Text size="sm" c="dimmed" fw={700}>Monto pendiente</Text>
            <Title order={3}>$ {formatearMonto(stats.totalPending)}</Title>
          </Card>
          <Card withBorder>
            <Text size="sm" c="dimmed" fw={700}>Convertidas</Text>
            <Title order={3}>{stats.converted}</Title>
          </Card>
        </SimpleGrid>

        {error && (
          <Alert icon={<IconAlertCircle size={18} />} color="red" mb="lg">
            {error}
          </Alert>
        )}

        {loading ? (
          <Group justify="center" py="xl">
            <Loader />
            <Text c="dimmed">Cargando solicitudes...</Text>
          </Group>
        ) : (
          <Table.ScrollContainer minWidth={980}>
            <Table verticalSpacing="md" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Solicitud</Table.Th>
                  <Table.Th>Cliente</Table.Th>
                  <Table.Th>Servicios</Table.Th>
                  <Table.Th>Total</Table.Th>
                  <Table.Th>Estado</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Acciones</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {requests.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text ta="center" c="dimmed" py="xl">Todavía no hay solicitudes web.</Text>
                    </Table.Td>
                  </Table.Tr>
                ) : requests.map((request) => {
                  const status = getStatusMeta(request.status);
                  const customer = request.customer || {};
                  const items = request.items || [];
                  const canConvert = request.status === 'paid'
                    || (request.status === 'pending_payment' && request.source !== 'web_mercadopago');

                  return (
                    <Table.Tr key={request.id}>
                      <Table.Td>
                        <Stack gap={2}>
                          <Text fw={800}>{request.id}</Text>
                          <Text size="xs" c="dimmed">{getFechaLegible(request.createdAt)}</Text>
                          {request.numeroId && <Badge color="blue" variant="light" w="fit-content">{request.numeroId}</Badge>}
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <Stack gap={2}>
                          <Text fw={700}>{customer.name || 'Sin nombre'}</Text>
                          <Text size="xs" c="dimmed">{customer.phone || 'Sin teléfono'}</Text>
                          <Text size="xs" c="dimmed">{customer.email || 'Sin email'}</Text>
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <Stack gap={4}>
                          {items.map((item) => (
                            <Text key={item.serviceId} size="sm">
                              {item.name} x{item.quantity}
                            </Text>
                          ))}
                          {request.coupon?.code && (
                            <Badge color="green" variant="light" w="fit-content">
                              Cupón {request.coupon.code}
                            </Badge>
                          )}
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <Text fw={900}>$ {formatearMonto(request.total)}</Text>
                        <Text size="xs" c="dimmed">Desc. $ {formatearMonto(request.discountAmount)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={status.color} variant="light">{status.label}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group justify="flex-end" gap="xs" wrap="nowrap">
                          <Button size="xs" variant="light" onClick={() => setSelectedRequest(request)}>
                            Ver
                          </Button>
                          <Button
                            size="xs"
                            variant="light"
                            color="gray"
                            leftSection={<IconCopy size={14} />}
                            onClick={() => handleCopySummary(request)}
                          >
                            Copiar
                          </Button>
                          {canConvert && (
                            <Button
                              size="xs"
                              color="green"
                              leftSection={<IconCheck size={14} />}
                              loading={convertingId === request.id}
                              onClick={() => handleConvert(request)}
                            >
                              Crear
                            </Button>
                          )}
                          {request.status === 'pending_payment' && (
                            <Button
                              size="xs"
                              color="red"
                              variant="light"
                              leftSection={<IconX size={14} />}
                              onClick={() => handleCancel(request)}
                            >
                              Cancelar
                            </Button>
                          )}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Paper>

      <Modal
        opened={Boolean(selectedRequest)}
        onClose={() => setSelectedRequest(null)}
        title="Detalle de solicitud"
        size="lg"
      >
        {selectedRequest && (
          <Stack>
            <Group justify="space-between">
              <Badge color={getStatusMeta(selectedRequest.status).color} variant="light">
                {getStatusMeta(selectedRequest.status).label}
              </Badge>
              <Text size="sm" c="dimmed">{getFechaLegible(selectedRequest.createdAt)}</Text>
            </Group>
            <Divider />
            <Text style={{ whiteSpace: 'pre-wrap' }}>{getRequestSummary(selectedRequest)}</Text>
            <Group justify="flex-end">
              <Button
                variant="light"
                leftSection={<IconCopy size={16} />}
                onClick={() => handleCopySummary(selectedRequest)}
              >
                Copiar resumen
              </Button>
              {(selectedRequest.status === 'paid'
                || (selectedRequest.status === 'pending_payment' && selectedRequest.source !== 'web_mercadopago')) && (
                <Button
                  color="green"
                  leftSection={<IconGiftCard size={16} />}
                  loading={convertingId === selectedRequest.id}
                  onClick={() => handleConvert(selectedRequest)}
                >
                  Marcar pagada y crear giftcard
                </Button>
              )}
            </Group>
          </Stack>
        )}
      </Modal>
    </Container>
  );
};

export default GiftcardRequests;
